import type { ChangeRequestDto } from "../../../api";

/**
 * Overlays a saved profile draft's proposed values onto form values built from
 * the live profile, so continuing a draft shows what the user saved rather
 * than starting over. Keys the draft doesn't touch keep their live value; ids
 * (countryId / stateId) come back as the strings the form fields use.
 */
export function withDraftValues<T extends Record<string, string>>(
  values: T,
  draft: ChangeRequestDto | null | undefined,
): T {
  const proposed = (draft?.proposedChanges ?? {}) as Record<string, unknown>;
  const next: Record<string, string> = { ...values };
  for (const key of Object.keys(values)) {
    if (!Object.prototype.hasOwnProperty.call(proposed, key)) continue;
    const raw = proposed[key];
    next[key] = raw === null || raw === undefined ? "" : String(raw);
  }
  return next as T;
}

/**
 * The proposed changes to send when updating a saved draft from a form that
 * only edits some of the profile's fields. The backend replaces a draft's
 * `proposedChanges` wholesale, so changes the form can't show (e.g. `name`
 * saved from the full change-request page) are carried over from the draft;
 * for the keys the form owns, the form's diff decides — a field set back to
 * its live value drops out of the request.
 */
export function mergeWithDraftChanges(
  draft: ChangeRequestDto | null | undefined,
  formKeys: readonly string[],
  formChanges: Record<string, unknown>,
): Record<string, unknown> {
  const carried: Record<string, unknown> = {
    ...((draft?.proposedChanges ?? {}) as Record<string, unknown>),
  };
  for (const key of formKeys) delete carried[key];
  return { ...carried, ...formChanges };
}
